import { krotkaNazwa } from './katalog'
import { liczbaMagazynow } from './projekt'

/**
 * Projekt 3D a karta klienta (decyzja właściciela 2026-10-11).
 *
 * Z karty do projektu: adres (do wyszukiwarki GUGiK), moc instalacji i pojemność magazynu, a jeśli klient ma już
 * zapisany projekt, cały projekt. Z projektu do karty: adres wskazanego miejsca, moc z układu modułów, pojemność
 * wybranego magazynu i sam projekt (`dane.projekt3d`). Puste pole karty uzupełniamy bez pytania. Gdy w karcie jest
 * inna wartość, pokazujemy „w karcie X, w projekcie Y”, a użytkownik wybiera: zostaw albo nadpisz.
 */

/** Backend przyjmuje do 64 KiB na całe `dane` klienta; zostawiamy zapas na resztę karty. */
export const LIMIT_DANYCH_B = 60 * 1024

export const POLA_ADRESU = ['ulica', 'nrDomu', 'kod', 'miejscowosc', 'gmina', 'powiat', 'wojewodztwo', 'nrDzialki']
const NAZWY_POL_ADRESU = {
  ulica: 'ulica', nrDomu: 'numer domu', kod: 'kod', miejscowosc: 'miejscowość', gmina: 'gmina', powiat: 'powiat', wojewodztwo: 'województwo', nrDzialki: 'działka',
}

const tekst = (v) => String(v ?? '').trim()
const porownywalny = (v) => tekst(v).toLowerCase().replace(/\s+/g, ' ')

/** Liczba z pola karty („8,5”, „8.5 kWp”) albo null. */
export const liczbaZKarty = (v) => {
  const m = String(v ?? '').replace(/\s/g, '').replace(',', '.').match(/^-?\d+(\.\d+)?/)
  const x = m ? Number(m[0]) : NaN
  return Number.isFinite(x) && x > 0 ? x : null
}

/** Rozmiar JSON w bajtach UTF-8 (tak liczy limit backend). */
export const bajty = (obiekt) => {
  const s = JSON.stringify(obiekt) ?? ''
  let n = 0
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i)
    if (c < 0x80) n += 1
    else if (c < 0x800) n += 2
    else if (c >= 0xd800 && c < 0xdc00) { n += 4; i += 1 } else n += 3
  }
  return n
}

const liczbaPl = (x) => x.toLocaleString('pl-PL', { maximumFractionDigits: 2 })

/** Adres jednym wierszem: „Słoneczna 4, 95-035 Ozorków”, „Leśmierz 10, 95-035” albo „dz. 79/4, Ozorków”. */
export const opisAdresu = (a = {}) => {
  const nr = tekst(a.nrDomu)
  const miejscowosc = tekst(a.miejscowosc)
  if (!tekst(a.ulica) && nr) return [`${miejscowosc} ${nr}`.trim(), tekst(a.kod)].filter(Boolean).join(', ')
  const ulica = [tekst(a.ulica), nr].filter(Boolean).join(' ')
  const dzialka = !nr && tekst(a.nrDzialki) ? `dz. ${tekst(a.nrDzialki)}` : ''
  return [ulica, dzialka, [tekst(a.kod), miejscowosc].filter(Boolean).join(' ')].filter(Boolean).join(', ')
}

// Zapytanie do wyszukiwarki GUGiK z adresu karty: `polaZAdresuKarty` i `zapytanieZPol` w adresSzukania.js.

/**
 * Wynik wyszukiwarki GUGiK zgodny z adresem karty: ta sama miejscowość i numer, a ulica równa albo będąca
 * końcówką pełnej nazwy z rejestru („Wyszyńskiego” w karcie, „kard. Stefana Wyszyńskiego” w GUGiK).
 * Wyszukiwarka dopasowuje luźno (na „Adama Asnyka 10” zwraca też „Adama Mickiewicza 10”), więc bez tego
 * użytkownik musiałby wybierać z listy adres, który karta już zna.
 */
export const zgodnyZAdresem = (w, a = {}) => {
  if (!w || !tekst(a.nrDomu) || porownywalny(w.numer) !== porownywalny(a.nrDomu)) return false
  if (porownywalny(w.miejscowosc) !== porownywalny(a.miejscowosc)) return false
  const ulicaKarty = porownywalny(a.ulica)
  const ulicaWyniku = porownywalny(w.ulica)
  if (!ulicaKarty) return !ulicaWyniku
  return ulicaWyniku === ulicaKarty || ulicaWyniku.endsWith(` ${ulicaKarty}`)
}

/** Dane klienta z karty w kształcie wejścia do Projektu 3D (stan nawigacji, bez danych osobowych). */
export const klientDoProjektu = ({ id, nazwa, dane = {} }) => ({
  id,
  nazwa,
  adres: dane.adres || {},
  mocPv: liczbaZKarty(dane.mocPv),
  pojemnosc: liczbaZKarty(dane.pojemnosc),
  projekt: dane.projekt3d?.projekt || null,
  // Profil energetyczny z karty (`dane.energia`, do 4 KiB): zużycie, profil, odbiorniki, ulga. Projekt startuje z niego
  // (`daneEkonomiiZEnergii`) i po zapisie odsyła zużycie i produkcję z cieniem (`lib/projekt3d/energiaProjektu`).
  energia: dane.energia && typeof dane.energia === 'object' ? dane.energia : null,
})

/**
 * Adres miejsca z wyniku wyszukiwarki GUGiK. Ulicę i numer bierzemy tylko wtedy, gdy wskazany budynek leży przy
 * wyszukanym punkcie (do `promien` m): klik w sąsiedni dom nie może przepisać do karty adresu z wyszukiwarki.
 */
export const adresZWyniku = (wynik, { e, n } = {}, promien = 60) => {
  if (!wynik) return null
  const blisko = !Number.isFinite(e) || !Number.isFinite(wynik.e) || Math.hypot(e - wynik.e, n - wynik.n) <= promien
  const a = {
    ulica: blisko ? wynik.ulica || '' : '',
    nrDomu: blisko ? wynik.numer || '' : '',
    kod: blisko ? wynik.kodPocztowy || '' : '',
    miejscowosc: wynik.miejscowosc || '',
    gmina: wynik.gmina || '',
    powiat: wynik.powiat || '',
    wojewodztwo: wynik.wojewodztwo || '',
    nrDzialki: blisko ? wynik.numerDzialki || '' : '',
  }
  return POLA_ADRESU.some((k) => a[k]) ? a : null
}

/** Co projekt wnosi do karty. `kwp` z układu modułów, `pojemnosc` z wybranego magazynu (kWh). */
export const zProjektu = ({ adres = null, kwp = 0, pojemnosc = 0 }) => ({
  adres: adres && POLA_ADRESU.some((k) => tekst(adres[k])) ? adres : null,
  mocPv: kwp > 0 ? Math.round(kwp * 100) / 100 : null,
  pojemnosc: pojemnosc > 0 ? Math.round(pojemnosc * 100) / 100 : null,
})

const JEDNOSTKI = { mocPv: ['Moc instalacji PV', 'kWp'], pojemnosc: ['Pojemność magazynu', 'kWh'] }

/**
 * Różnice między kartą a projektem: `[{ pole, etykieta, wKarcie, wProjekcie, konflikt }]`.
 * `konflikt`: karta ma już inną wartość (trzeba zapytać); bez konfliktu pole tylko się uzupełni.
 * Adres porównujemy jako całość: jeden wiersz w oknie, choć w karcie to kilka pól.
 */
export const roznice = (dane = {}, z) => {
  const wynik = []
  const adresKarty = dane.adres || {}
  if (z.adres) {
    const inne = POLA_ADRESU.filter((k) => tekst(z.adres[k]) && porownywalny(adresKarty[k]) !== porownywalny(z.adres[k]))
    if (inne.length) {
      const konflikt = inne.some((k) => tekst(adresKarty[k]))
      const wiersz = { pole: 'adres', etykieta: 'Adres instalacji', wKarcie: opisAdresu(adresKarty), wProjekcie: opisAdresu(z.adres), konflikt }
      // Ten sam adres w karcie, brakuje tylko pól spoza wiersza adresu (gmina, powiat): pokazujemy, co dopiszemy.
      if (!konflikt && wiersz.wKarcie && wiersz.wKarcie === wiersz.wProjekcie) {
        wiersz.dopiszemy = inne.map((k) => `${NAZWY_POL_ADRESU[k]} ${tekst(z.adres[k])}`).join(', ')
      }
      wynik.push(wiersz)
    }
  }
  for (const pole of ['mocPv', 'pojemnosc']) {
    const p = z[pole]
    if (p == null) continue
    const k = liczbaZKarty(dane[pole])
    if (k != null && Math.abs(k - p) < 0.005) continue
    const [etykieta, jednostka] = JEDNOSTKI[pole]
    wynik.push({ pole, etykieta, wKarcie: k != null ? `${liczbaPl(k)} ${jednostka}` : '', wProjekcie: `${liczbaPl(p)} ${jednostka}`, konflikt: k != null })
  }
  return wynik
}

/**
 * Nowe `dane` karty. Pola bez konfliktu uzupełnione, konflikty według `nadpisz[pole]`; reszta karty bez zmian
 * (także pola, których formularz karty nie zna). Nadpisanie adresu pełnym adresem z projektu (z numerem domu
 * albo działką) czyści ulicę i numer z karty, których projekt nie ma: inaczej zostałaby ulica starego adresu.
 */
export const scalZKarta = (dane = {}, z, nadpisz = {}, projekt3d = undefined) => {
  const d = { ...dane, adres: { ...(dane.adres || {}) } }
  for (const r of roznice(dane, z)) {
    if (r.konflikt && !nadpisz[r.pole]) continue
    if (r.pole === 'adres') {
      const pelny = tekst(z.adres.nrDomu) || tekst(z.adres.nrDzialki)
      for (const k of POLA_ADRESU) {
        if (tekst(z.adres[k])) d.adres[k] = z.adres[k]
        else if (r.konflikt && pelny && (k === 'ulica' || k === 'nrDomu')) d.adres[k] = ''
      }
    } else {
      d[r.pole] = String(z[r.pole]).replace('.', ',')
    }
  }
  if (projekt3d !== undefined) d.projekt3d = projekt3d
  return d
}

/**
 * Projekt do zapisu w karcie: bez rzeczy, które da się odtworzyć (czas, obrys z ewidencji przy dużym
 * rozmiarze). Gdy i tak przekracza limit karty, zwraca null (zapisze się samo podsumowanie).
 */
export const projektDoKarty = (projekt, limit = 40 * 1024) => {
  const p = { ...projekt }
  delete p.czas
  delete p.klient
  if (bajty(p) <= limit) return p
  if (p.lokalizacja?.obrys) p.lokalizacja = { ...p.lokalizacja, obrys: null }
  if (bajty(p) <= limit) return p
  return null
}

/** Nazwy z katalogu bywają sklepowe („Moduł Fotowoltaiczny LONGI …”): producent raz, bez ogólnych słów. */
export const nazwaSprzetu = (producent, nazwa) => [producent, nazwa ? krotkaNazwa(nazwa, producent) : ''].filter(Boolean).join(' ')

const SEGMENT_MODELU = { panels: 'panel', inverter: 'inwerter', battery: 'magazyn' }

/** Slug do linku modelu. Strona modelu czyta tylko id na końcu, slug jest dla czytelności adresu. */
const slug = (t) => String(t || '')
  .replace(/ł/g, 'l').replace(/Ł/g, 'L')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'model'

const pozycja = (category, model, nazwa, quantity, spec) => ({
  category,
  model_id: model?.id ?? null,
  model_name: nazwa,
  slug: model?.id ? slug(nazwa) : null,
  model_url: model?.id ? `/model/${SEGMENT_MODELU[category]}/${slug(nazwa)}-${model.id}` : null,
  img_url: model?.img || null,
  quantity,
  unit_price_pln: null,
  line_price_pln: null,
  spec,
})

/**
 * Zestaw klienta z projektu w kształcie wariantu asystenta (`payload` zapisanego zestawu). Pozycje z katalogu
 * mają `model_id`, więc karta linkuje do modelu, a zgłoszenie do OSD bierze sprzęt; wpisane ręcznie mają samą
 * nazwę. `summary` czyta backend jako moc i pojemność klienta, gdy w karcie ich nie ma. Bez cen: projekt nie
 * wycenia sprzętu. Zwraca null, gdy nie ma żadnej pozycji.
 */
export const zestawZProjektu = ({ projekt, modul, liczbaModulow, nazwa }) => {
  const items = []
  const opisy = []
  const nazwa2 = nazwaSprzetu
  if (liczbaModulow > 0) {
    const nazwaModulu = nazwa2(modul.producent, modul.nazwa) || `Moduł ${modul.moc} W`
    items.push(pozycja('panels', projekt.moduly?.model, nazwaModulu, liczbaModulow, { power_w: modul.moc }))
    opisy.push(`${liczbaModulow} × ${nazwaModulu}${modul.nazwa && !String(modul.nazwa).includes(String(modul.moc)) ? ` ${modul.moc} W` : ''}`)
  }
  const f = projekt.falownik?.model
  if (f && (f.id || f.nazwa || f.producent)) {
    const nazwaF = f.nazwa ? nazwa2(f.producent, f.nazwa) : [f.producent, f.mocKw ? `${liczbaPl(f.mocKw)} kW` : ''].filter(Boolean).join(' ')
    items.push(pozycja('inverter', f, nazwaF, 1, { power_kw: f.mocKw ?? null, phase: f.fazy ?? null }))
    opisy.push(`Falownik ${nazwaF}`)
  }
  const pojemnosc = projekt.magazyn?.pojemnosc || 0
  const sztukMagazynu = liczbaMagazynow(projekt.magazyn)
  const pojemnoscSztuki = Math.round((pojemnosc / sztukMagazynu) * 100) / 100
  if (pojemnosc > 0) {
    const m = projekt.magazyn.model
    const nazwaM = (m?.nazwa ? nazwa2(m.producent, m.nazwa) : '') || `Magazyn energii ${liczbaPl(pojemnosc)} kWh`
    items.push(pozycja('battery', m, nazwaM, sztukMagazynu, { capacity_kwh: pojemnoscSztuki }))
    opisy.push(m?.nazwa ? `${sztukMagazynu > 1 ? `${sztukMagazynu} × ` : ''}${nazwaM}, ${liczbaPl(pojemnosc)} kWh` : nazwaM)
  }
  if (!items.length) return null
  const kwp = Math.round(liczbaModulow * modul.moc / 10) / 100
  return {
    nazwa: String(nazwa || 'Projekt 3D').slice(0, 120),
    pozycje: opisy,
    payload: {
      variant: 'projekt_3d',
      variant_label_pl: 'Projekt 3D',
      items,
      totals: { products_pln: null, shipping_pln: null, shipping_is_estimate: false, total_pln: null, sellers_count: 0, deliveries_count: 0 },
      summary: {
        panels_total_kwp: kwp > 0 ? kwp : null,
        modules_count: liczbaModulow,
        inverter_power_kw: f?.mocKw ?? null,
        inverter_count: f ? 1 : 0,
        inverter_power_total_kw: f?.mocKw ?? null,
        battery_capacity_kwh: pojemnoscSztuki || null,
        battery_count: pojemnosc > 0 ? sztukMagazynu : 0,
        battery_capacity_total_kwh: pojemnosc || null,
      },
    },
  }
}

const KATEGORIA_CENY = { panels: 'panel', inverter: 'inwerter', battery: 'magazyn' }

/**
 * Zestaw z Projektu 3D z cenami sprzętu (prośba właściciela 2026-10-30): bez nich karta klienta nie policzy ceny całości.
 * Cena sztuki: z zestawu na tej karcie, gdy ma ten sam model (cena z asystenta), inaczej najniższa z katalogu przez
 * `cenaZKatalogu(kategoria, id)`. Sumę podajemy tylko przy komplecie cen, jak dobór ręczny; dostawa nieznana.
 */
export const zestawZCenami = async (payload, zestawyKarty, cenaZKatalogu) => {
  const zKarty = (zestawyKarty || []).flatMap((z) => z?.payload?.items || [])
  const items = await Promise.all((payload?.items || []).map(async (it) => {
    if (it.model_id == null) return it
    const znana = zKarty.find((x) => x.category === it.category && String(x.model_id) === String(it.model_id) && Number(x.unit_price_pln) > 0)
    const cena = znana ? Number(znana.unit_price_pln) : await cenaZKatalogu(KATEGORIA_CENY[it.category], it.model_id)
    return Number(cena) > 0
      ? { ...it, unit_price_pln: Number(cena), line_price_pln: Math.round(Number(cena) * (it.quantity || 1) * 100) / 100 }
      : it
  }))
  const ceny = items.map((i) => i.line_price_pln)
  const komplet = ceny.length > 0 && ceny.every((c) => Number(c) > 0)
  const koszt = komplet ? Math.round(ceny.reduce((a, c) => a + c, 0)) : null
  return { ...payload, items, totals: { ...(payload?.totals || {}), products_pln: koszt, total_pln: koszt } }
}
